import React from 'react';
import { motion } from 'motion/react';
import { Download, Search } from 'lucide-react';
import { Capacitor } from '@capacitor/core';

interface StorefrontHeroProps {
  settings: any;
  LOGO_FULL: string;
  search: string;
  setSearch: (val: string) => void;
}

export const StorefrontHero = ({ settings, LOGO_FULL, search, setSearch }: StorefrontHeroProps) => {
  return (
    <section className="mb-20 relative overflow-hidden rounded-[4rem] bg-[#020617] py-24 px-6 md:px-16 shadow-2xl premium-shadow group">
      {/* Cinematic Background */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-0 right-0 w-[80%] h-[80%] bg-indigo-600/20 blur-[140px] rounded-full translate-x-1/2 -translate-y-1/2 animate-pulse" />
        <div className="absolute bottom-0 left-0 w-[60%] h-[60%] bg-blue-600/10 blur-[120px] rounded-full -translate-x-1/2 translate-y-1/2 animate-pulse [animation-delay:3s]" />
        <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
      </div>
      
      <div className="relative z-10 flex flex-col md:flex-row items-center gap-12 max-w-6xl mx-auto">
        <div className="flex-1 text-left space-y-8">
          <motion.div
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            className="inline-flex items-center gap-3 px-4 py-2 bg-indigo-500/10 border border-indigo-500/20 rounded-full"
          >
            <div className="w-2 h-2 bg-indigo-500 rounded-full animate-ping" />
            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-indigo-400 font-display">Est. 2005 • Global Quality</span>
          </motion.div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="space-y-4"
          >
            <h1 className="text-5xl sm:text-7xl md:text-8xl font-black text-white tracking-tighter uppercase leading-tight md:leading-[0.85] font-display">
              Precision <br />
              <span className="text-indigo-500 italic">Instruments</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-400 max-w-lg font-medium leading-relaxed font-sans">
              Next-generation surgical solutions engineered for the world's most demanding procedures.
            </p>
          </motion.div>

          {/* Integrated Search */}
          <div className="relative max-w-md group/search">
            <div className="absolute inset-y-0 left-6 flex items-center pointer-events-none">
              <Search size={22} className="text-slate-500 group-focus-within/search:text-indigo-500 transition-colors" />
            </div>
            <input
              type="text"
              placeholder="Quick search catalog..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-16 pr-6 py-6 bg-white/5 border border-white/10 rounded-3xl text-white outline-none focus:ring-4 focus:ring-indigo-500/20 focus:bg-white/10 transition-all font-bold placeholder:text-slate-600"
            />
          </div>
        </div>

        <motion.div 
          initial={{ scale: 0.9, opacity: 0, rotate: 5 }}
          animate={{ scale: 1, opacity: 1, rotate: 0 }}
          className="flex-1 flex justify-center perspective-[1000px] hidden md:flex"
        >
          <div className="relative group/logo">
            <div className="absolute inset-0 bg-indigo-500/20 blur-[100px] rounded-full group-hover/logo:bg-indigo-500/40 transition-all duration-700" />
            <img 
              src={settings.logoUrl || LOGO_FULL} 
              alt="Logo" 
              className="w-72 h-72 object-contain relative z-10 transition-transform duration-700 group-hover/logo:scale-110 drop-shadow-[0_20px_50px_rgba(99,102,241,0.3)] filter brightness-110"
              onError={(e) => { e.currentTarget.src = LOGO_FULL; }}
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};
